import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Sprout, Leaf, Sparkles, ShieldCheck, Droplets, Heart, Compass } from "lucide-react";
import { MaskedLines, Reveal, Eyebrow } from "../components/motion";
import { Marquee } from "../components/Marquee";
import { ResultsSection } from "../components/ResultsSection";
import { ProductCard } from "../components/ProductCard";
import { useProducts } from "../hooks/useProducts";
import { HERO_IMAGE } from "../config/site";

const BENEFITS = [
  { icon: ShieldCheck, text: "Aiuta a ridurre la caduta eccessiva" },
  { icon: Sprout, text: "Supporta la fase di crescita del capello (anagen)" },
  { icon: Sparkles, text: "Aiuta a migliorare l'aspetto delle zone diradate" },
  { icon: Droplets, text: "Aiuta a riequilibrare il cuoio capelluto senza aggredirlo" },
];

const INGREDIENTS = [
  {
    name: "Oleum Rosmarini Officinalis",
    desc: "Ricco di acido rosmarinico, è utilizzato in cosmetica per la cura del cuoio capelluto e la microcircolazione cutanea.",
    highlight: "Supporta la vitalità del cuoio capelluto e il benessere del follicolo.",
  },
  {
    name: "Botanical Complex ricco in Thymoquinone",
    desc: "Attivo botanico dalle proprietà riequilibranti, pensato per la cute stressata.",
    highlight: "Aiuta a mantenere il cuoio capelluto equilibrato e a prendersi cura dei capelli indeboliti.",
  },
  {
    name: "Olio di Prunus Amygdalus Dulcis",
    desc: "Fonte naturale di vitamina E e acidi grassi essenziali.",
    highlight: "Nutre, ammorbidisce e aiuta a mantenere i capelli più forti e dall'aspetto sano.",
  },
];

const PAINS = [
  "Trovi sempre più capelli sulla spazzola",
  "Le tempie sembrano più vuote",
  "I tuoi capelli sono sempre fermi alla stessa lunghezza e vuoi sbloccare la loro crescita",
];

export default function Home() {
  const { products } = useProducts();
  const featured = products.filter((p) => p.featured);
  const kits = products.filter((p) => p.category === "kit");
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imageY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);

  return (
    <main data-testid="home-page">
      {/* HERO */}
      <section ref={heroRef} className="relative overflow-hidden bg-gradient-to-b from-lilac-soft/70 to-transparent">
        <div className="max-w-7xl mx-auto px-6 pt-16 pb-20 lg:pt-32 lg:pb-36 grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <div>
            <Reveal>
              <Eyebrow data-testid="hero-eyebrow">Trattamento Rapida Crescita</Eyebrow>
            </Reveal>
            <MaskedLines
              as="h1"
              delay={0.15}
              lines={["Riduci la caduta", "dei tuoi capelli"]}
              className="mt-5 font-serif text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight text-plum leading-[1.08]"
              lineClassName=""
            />
            <Reveal delay={0.6}>
              <p className="mt-3 font-serif italic text-xl sm:text-2xl text-matcha">
                con il Trattamento Rapida Crescita
              </p>
              <p className="mt-6 text-lg lg:text-xl text-plum/60 leading-relaxed lg:leading-loose max-w-md lg:max-w-lg">
                Una formula botanica ad alta efficacia pensata per prendersi cura del cuoio capelluto,
                ridurre la caduta e sostenere il naturale ciclo di crescita del capello.
              </p>
            </Reveal>
            <Reveal delay={0.8} className="mt-8 flex flex-col sm:flex-row gap-3">
              <Link
                to="/prodotto/trattamento-rapida-crescita"
                data-testid="hero-cta-scopri"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-lilac text-white font-bold text-sm uppercase tracking-[0.14em] hover:bg-lilac-dark hover:shadow-[0_16px_40px_-12px_rgba(139,92,246,0.6)] active:scale-95 transition-all duration-300"
              >
                Scopri il trattamento <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/prodotto/trattamento-rapida-crescita#come-si-applica"
                data-testid="hero-cta-come-funziona"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full border-2 border-plum/15 text-plum font-bold text-sm uppercase tracking-[0.14em] hover:border-lilac-dark hover:text-matcha transition-all duration-300"
              >
                Come funziona?
              </Link>
            </Reveal>
          </div>
          <motion.div style={{ y: imageY }} className="relative" initial={{ opacity: 0, scale: 0.94 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1.1, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}>
            <div className="absolute -top-6 -left-6 w-40 h-40 rounded-full bg-lilac-light/60 blur-2xl" />
            <div className="relative rounded-[3rem] rounded-tr-[8rem] overflow-hidden shadow-[0_40px_80px_-30px_rgba(46,16,101,0.35)] border-4 border-white">
              <img
                src={HERO_IMAGE}
                alt="I tre percorsi del Trattamento Rapida Crescita"
                className="w-full aspect-[4/4.4] object-cover"
                data-testid="hero-image"
              />
            </div>
            <motion.div
              className="absolute -bottom-5 -left-4 sm:-left-8 bg-white rounded-2xl shadow-xl px-5 py-4 flex items-center gap-3"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.1, duration: 0.7 }}
            >
              <Heart className="w-5 h-5 text-matcha" />
              <div>
                <p className="text-xs font-bold text-plum uppercase tracking-wide">Oltre 53 donne</p>
                <p className="text-[11px] text-plum/50">hanno già iniziato il percorso</p>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* PROBLEMA */}
      <section className="py-16 sm:py-20">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <Reveal>
            <Eyebrow className="justify-center">Ti riconosci?</Eyebrow>
            <h2 className="mt-4 font-serif text-2xl sm:text-3xl lg:text-4xl text-plum tracking-tight">
              Quando i capelli cadono, il corpo sta parlando. Impara ad ascoltarlo.
            </h2>
          </Reveal>
          <div className="mt-10 flex flex-wrap justify-center gap-3">
            {PAINS.map((pain, i) => (
              <Reveal key={pain} delay={i * 0.08}>
                <span className="inline-block bg-mist border border-lilac-light text-plum/80 text-sm px-5 py-3 rounded-full">
                  {pain}
                </span>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.3}>
            <p className="mt-10 text-base sm:text-lg text-plum/60 max-w-2xl mx-auto leading-relaxed">
              Tutto parte dal tuo cuoio capelluto. Quando è stressato, squilibrato o poco ossigenato,
              il capello ne risente. Il primo gesto di cura è prendertene cura davvero.
            </p>
          </Reveal>
        </div>
      </section>

      {/* FORMULA BOTANICA */}
      <section className="py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-6">
          <Reveal className="max-w-2xl">
            <Eyebrow>Gli attivi</Eyebrow>
            <h2 className="mt-4 font-serif text-2xl sm:text-3xl lg:text-4xl text-plum tracking-tight">
              La forza della formula botanica
            </h2>
            <p className="mt-4 text-base sm:text-lg text-plum/60 leading-relaxed">
              Una combinazione di attivi botanici selezionati per prendersi cura del tuo cuoio capelluto e dei tuoi capelli.
            </p>
          </Reveal>
          <div className="mt-12 grid md:grid-cols-3 gap-5">
            {INGREDIENTS.map((ing, i) => (
              <Reveal key={ing.name} delay={i * 0.1}>
                <div className="relative bg-plum rounded-3xl p-7 h-full overflow-hidden group">
                  <span className="absolute -top-4 -right-2 font-serif text-[7rem] leading-none text-white/5 select-none">
                    0{i + 1}
                  </span>
                  <Sprout className="w-6 h-6 text-lilac" />
                  <h3 className="mt-4 font-serif text-lg text-white leading-snug">{ing.name}</h3>
                  <p className="mt-3 text-sm text-white/60 leading-relaxed">{ing.desc}</p>
                  <p className="mt-4 text-sm text-lilac-light italic leading-relaxed flex gap-2">
                    <Leaf className="w-4 h-4 shrink-0 mt-0.5" /> {ing.highlight}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <Marquee />

      {/* QUIZ TEASER */}
      <section className="py-16 sm:py-24">
        <div className="max-w-5xl mx-auto px-6">
          <Reveal>
            <div className="relative bg-gradient-to-br from-lilac to-lilac-dark rounded-[2.5rem] p-8 sm:p-14 text-center overflow-hidden">
              <div className="absolute -top-10 -right-10 w-48 h-48 rounded-full bg-white/10 blur-xl" />
              <div className="absolute -bottom-12 -left-8 w-56 h-56 rounded-full bg-plum/20 blur-xl" />
              <Compass className="w-10 h-10 text-highlight mx-auto" />
              <h2 className="mt-5 font-serif text-2xl sm:text-3xl lg:text-4xl text-highlight tracking-tight">
                Scegli il percorso più adatto a te
              </h2>
              <p className="mt-4 text-white/80 text-base sm:text-lg max-w-xl mx-auto leading-relaxed">
                Rispondi a tre semplici domande e scopri il prodotto o la routine pensata per la tua esigenza.
              </p>
              <Link
                to="/quiz"
                data-testid="quiz-teaser-cta"
                className="inline-flex items-center gap-2 mt-8 px-8 py-4 rounded-full bg-white text-plum font-bold text-sm uppercase tracking-[0.14em] hover:scale-105 active:scale-95 transition-transform duration-300"
              >
                Inizia il quiz <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* PRODOTTI */}
      <section className="py-16 sm:py-24 bg-mist/60">
        <div className="max-w-7xl mx-auto px-6">
          <Reveal className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <Eyebrow>Lo shop</Eyebrow>
              <h2 className="mt-4 font-serif text-2xl sm:text-3xl lg:text-4xl text-plum tracking-tight">
                Costruisci il tuo rituale
              </h2>
            </div>
            <Link to="/shop" data-testid="home-shop-link" className="text-sm font-bold uppercase tracking-[0.14em] text-matcha hover:text-plum transition-colors flex items-center gap-1">
              Vedi tutto <ArrowRight className="w-4 h-4" />
            </Link>
          </Reveal>
          <div className="mt-10 grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            {featured.map((p, i) => (
              <ProductCard key={p.slug} product={p} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* PERCORSI */}
      <section className="py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-6">
          <Reveal className="text-center max-w-2xl mx-auto">
            <Eyebrow>Routine & Kit</Eyebrow>
            <h2 className="mt-4 font-serif text-2xl sm:text-3xl lg:text-4xl text-plum tracking-tight">
              Tre percorsi, un solo obiettivo: i tuoi capelli
            </h2>
            <p className="mt-4 text-base sm:text-lg text-plum/60">
              Non semplici prodotti raggruppati, ma veri percorsi di cura pensati per accompagnarti nel tempo.
            </p>
          </Reveal>
          <div className="mt-12 grid sm:grid-cols-3 gap-5">
            {kits.map((kit, i) => (
              <ProductCard key={kit.slug} product={kit} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* PRIMA / DOPO */}
      <ResultsSection />

      {/* CTA FINALE */}
      <section className="px-6 pb-20">
        <Reveal>
          <div className="max-w-5xl mx-auto bg-lilac rounded-[2.5rem] p-10 sm:p-16 text-center texture-grain relative overflow-hidden">
            <Sprout className="w-8 h-8 text-highlight mx-auto" />
            <h2 className="mt-4 font-serif text-3xl sm:text-4xl lg:text-5xl text-highlight tracking-tight">
              Pronta a prenderti cura dei tuoi capelli?
            </h2>
            <p className="mt-4 text-white/85 text-base sm:text-lg max-w-xl mx-auto">
              Scopri il Trattamento Rapida Crescita e inseriscilo nella tua routine di cura del cuoio capelluto.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row justify-center gap-3">
              <Link
                to="/prodotto/trattamento-rapida-crescita"
                data-testid="final-cta-acquista"
                className="px-8 py-4 rounded-full bg-white text-plum font-bold text-sm uppercase tracking-[0.14em] hover:scale-105 active:scale-95 transition-transform duration-300"
              >
                Acquista ora
              </Link>
              <Link
                to="/prodotto/trattamento-rapida-crescita#come-si-applica"
                data-testid="final-cta-utilizzo"
                className="px-8 py-4 rounded-full border-2 border-white/60 text-white font-bold text-sm uppercase tracking-[0.14em] hover:bg-white/10 transition-colors duration-300"
              >
                Scopri come utilizzarlo
              </Link>
            </div>
          </div>
        </Reveal>
      </section>
    </main>
  );
}
